// ============================================================
// Wauvea WebXR — XR Session Manager
// WebXR session entry/exit
// ============================================================

export class XRSession {
  constructor(renderer) {
    this.renderer = renderer;
    this.session = null;
    this.isActive = false;
    this.onSessionStart = null;
    this.onSessionEnd = null;
  }

  /**
   * Check if immersive VR is supported
   * @returns {Promise<boolean>}
   */
  async isSupported() {
    if (!navigator.xr) return false;
    try {
      return await navigator.xr.isSessionSupported('immersive-vr');
    } catch {
      return false;
    }
  }

  /**
   * Request and start an immersive VR session
   */
  async enter() {
    if (this.session) return;

    try {
      const session = await navigator.xr.requestSession('immersive-vr', {
        optionalFeatures: ['local-floor', 'bounded-floor', 'hand-tracking'],
      });

      this.session = session;
      this.isActive = true;

      session.addEventListener('end', () => {
        this.session = null;
        this.isActive = false;
        this.onSessionEnd?.();
      });

      this.renderer.xr.setSession(session);
      this.onSessionStart?.();
    } catch (err) {
      console.error('[XR] Session error:', err);
      throw err;
    }
  }

  /**
   * End the current VR session
   */
  async exit() {
    if (this.session) {
      await this.session.end();
    }
  }

  /**
   * Toggle VR session
   */
  async toggle() {
    if (this.isActive) {
      await this.exit();
    } else {
      await this.enter();
    }
  }
}
